/**
 * npb.jp データ取得の Cloudflare Pages Functions エンドポイント
 * （`/api/players`・`/api/players/:id`。ADR-0007 / Issue #7・#9）への
 * フロントエンド側クライアント。
 *
 * 外部データ取得は `lib/` 層に閉じ込め、`ui/`・`features/` から直接 fetch しない
 * （SPEC §7.4 / CLAUDE.md 禁止事項5）。`functions/` 側の内部型（`functions/_lib/npb/types.ts`）
 * とは意図的に分離している（Issue #7 と同じ方針）。
 */

export interface RosterPlayer {
  playerId: string
  name: string
  nameKana: string | null
  team: string
  teamCode: string
  uniformNumber: string | null
  position: string | null
}

export interface BattingSeasonStats {
  year: string
  team: string
  games: number
  plateAppearances: number
  atBats: number
  runs: number
  hits: number
  doubles: number
  triples: number
  homeRuns: number
  totalBases: number
  runsBattedIn: number
  stolenBases: number
  caughtStealing: number
  sacrificeHits: number
  sacrificeFlies: number
  walks: number
  hitByPitch: number
  strikeouts: number
  doublePlays: number
  battingAverage: string
  sluggingPercentage: string
  onBasePercentage: string
}

export interface PitchingSeasonStats {
  year: string
  team: string
  appearances: number
  wins: number
  losses: number
  saves: number
  holds: number
  holdPoints: number
  completeGames: number
  shutouts: number
  noWalkGames: number
  winningPercentage: string
  battersFaced: number
  inningsPitched: string
  hitsAllowed: number
  homeRunsAllowed: number
  walks: number
  hitByPitch: number
  strikeouts: number
  wildPitches: number
  balks: number
  runsAllowed: number
  earnedRuns: number
  earnedRunAverage: string
}

export interface PlayerProfile {
  playerId: string
  uniformNumber: string | null
  team: string | null
  name: string
  nameKana: string | null
  position: string | null
  throwsBats: string | null
  heightWeight: string | null
  birthDate: string | null
  career: string | null
  draft: string | null
}

export interface PlayerSeasonData {
  playerId: string
  profile: PlayerProfile
  latestBattingStats: BattingSeasonStats | null
  latestPitchingStats: PitchingSeasonStats | null
  fetchedAt: string
}

export type NpbApiResult<T> =
  | { ok: true; data: T }
  | { ok: false; error: string; reason: string }

interface ErrorResponseBody {
  error?: string
  reason?: string
}

const GENERIC_ERROR_MESSAGE = '通信に失敗しました。しばらくしてから再度お試しください。'

async function readErrorBody(response: Response): Promise<{ error: string; reason: string }> {
  try {
    const body = (await response.json()) as ErrorResponseBody
    return { error: body.error ?? GENERIC_ERROR_MESSAGE, reason: body.reason ?? 'unexpected_error' }
  } catch {
    return { error: GENERIC_ERROR_MESSAGE, reason: 'unexpected_error' }
  }
}

/** ロースター（全球団統合の選手一覧）を取得する（`GET /api/players`）。例外は投げない */
export async function fetchRoster(): Promise<NpbApiResult<RosterPlayer[]>> {
  try {
    const response = await fetch('/api/players')
    if (!response.ok) {
      const { error, reason } = await readErrorBody(response)
      return { ok: false, error, reason }
    }
    const body = (await response.json()) as { players: RosterPlayer[] }
    return { ok: true, data: body.players }
  } catch {
    return { ok: false, error: GENERIC_ERROR_MESSAGE, reason: 'network_error' }
  }
}

/** 選手個別のシーズン成績を取得する（`GET /api/players/:id`）。例外は投げない */
export async function fetchPlayerSeasonData(playerId: string): Promise<NpbApiResult<PlayerSeasonData>> {
  try {
    const response = await fetch(`/api/players/${encodeURIComponent(playerId)}`)
    if (!response.ok) {
      const { error, reason } = await readErrorBody(response)
      return { ok: false, error, reason }
    }
    const data = (await response.json()) as PlayerSeasonData
    return { ok: true, data }
  } catch {
    return { ok: false, error: GENERIC_ERROR_MESSAGE, reason: 'network_error' }
  }
}
